<template>
  <SDialog :open="modalVisibility" @update:open="onOpenChange">
    <SDialogScrollContent
      class="z-[2100] w-[850px] max-w-[850px]"
      data-cy="pos-config-modal"
      @escape-key-down.prevent
      @pointer-down-outside.prevent
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle>{{ $t("posconfiguration.title") }}</SDialogTitle>
      </SDialogHeader>
      <SSeparator class="my-5" />

      <div
        v-if="
          ((!configs || !configs.length) && !loading) || adminUserWithoutGroup
        "
        class="text-sm text-[#0a0a0a]"
      >
        {{ $t("posconfiguration.nosettings") }}
      </div>

      <div
        v-else-if="loading || configLoading"
        class="flex flex-col items-center justify-center text-sm text-[#0a0a0a]"
      >
        <p>{{ getLoadingText }}</p>
        <Spinner v-if="loading" />
        <div v-else class="flex w-[350px] items-center gap-3 pt-5 pl-5">
          <SProgress :model-value="progressValue" class="flex-1" />
          <span class="w-10 text-right text-sm tabular-nums text-gray-700">
            {{ progressValue }}%
          </span>
        </div>
      </div>

      <div v-else>
        <p
          v-if="configs && configs.length && !loading"
          class="text-sm text-[#0a0a0a]"
        >
          {{ $t("posconfiguration.text") }}
        </p>
        <div class="mt-4 grid grid-cols-3 gap-4">
          <div
            v-for="config in configs"
            :key="config.id"
            class="flex min-h-[110px] min-w-0 cursor-pointer flex-col justify-center rounded-md bg-[#ededed] p-4 text-center shadow-[inset_0_-4px_0_#57a8b9] transition-shadow duration-150"
            @click="confirmSelectConfig(config)"
            data-cy="pos-config-card"
          >
            <p
              class="mb-1.5 break-all text-[17px] font-bold leading-snug text-gray-800 line-clamp-2"
            >
              {{ config.name }}
            </p>
            <p
              class="break-all text-[13px] leading-tight text-gray-500 line-clamp-1"
            >
              {{ config.description }}
            </p>
          </div>
        </div>
      </div>
    </SDialogScrollContent>
  </SDialog>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";

//common
import { useI18n } from "vue-i18n";

// modules
import UserModule from "@/store/modules/user";
import POSConfigModule from "@/store/modules/pos-config";

// interfaces
import { IPOSConfig } from "@/types/db/pos-config";

// helpers
import {
  pushWithQuery,
  redirectToLoginPage,
} from "@/utils/helpers/router.helper";

import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import NotificationService from "@/services/notification.service";

// constants
import { ModalStatus } from "@/constants/modal";

import { RouteNames } from "@/constants/router";

// components
import { Spinner } from "@/components/index";
import AuthModule from "@/store/modules/auth";
import AppModule from "@/store/modules/app";
import DeviceModule from "@/store/modules/device";
import { LogService } from "@/services/logging";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import ModalModule from "@/store/modules/modal";
import {
  createNewOrder,
  ensureSelectedOrder,
  hasNonEmptyOrders,
  removeEmptyOrders,
} from "@/utils/vue-helpers/orders.helper";
import { proceedFetchAndStoreGroupPicture } from "@/utils/helpers/product-category";
import { NetworkErrors } from "@/constants/errors/network";
import { hasSalesStarted } from "@/utils/vue-helpers/cash-float";
import { loadConfigData } from "@/utils/helpers/pos-config";
import { checkUserSocialSecurityNumber } from "@/utils/helpers/auth";
import {
  SDialog,
  SDialogHeader,
  SDialogScrollContent,
  SDialogTitle,
  SProgress,
  SSeparator,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "PosConfiguration",
  components: {
    Spinner,
    SDialog,
    SDialogHeader,
    SDialogScrollContent,
    SDialogTitle,
    SProgress,
    SSeparator,
  },
  setup() {
    const { t } = useI18n({});
    let loading = ref(true);
    const userGroupId = ref(UserModule.getUserGroupId);
    let configs = ref([] as IPOSConfig[]); // ref(ConfigurationModule.posConfigs);

    const modalVisibility = dialogVisibleForStatus(ModalStatus.PosConfig);
    const abortController = ref<AbortController | null>(null);

    const adminUserWithoutGroup = computed(() => {
      return UserModule.isAdminUserWithoutGroup;
    });

    const getLoadingText = computed(() => {
      return POSConfigModule.getLoadingText;
    });

    const progressValue = computed(() => {
      let pcsProgress = POSConfigModule.pcsLoaded;
      let timeslotsProgress = POSConfigModule.timeslotsLoaded;
      if (pcsProgress > 50 || timeslotsProgress) {
        // Cap PCs progress at 50 once timeslots start loading
        pcsProgress = 50;
      }
      if (timeslotsProgress > 35) {
        // Cap timeslots progress at 35
        timeslotsProgress = 35;
      }
      // extraProgress covers the remaining 5% (seating plans, promotions, offers, locations)
      return (
        10 + pcsProgress + timeslotsProgress + POSConfigModule.extraProgress
      );
    });

    // const progressValue = ref<number>(0);
    const configLoading = ref(false);

    async function handleBeforeClose(done: () => void) {
      if (!configLoading.value) {
        done();
        return;
      }
      const confirmed = await showConfirm({
        title: t("common.warning"),
        message: t("config.abort-reload-warning"),
        variant: "destructive",
        confirmText: t("common.yes"),
        cancelText: t("common.no"),
      });

      if (confirmed) {
        abortController.value?.abort();
        done();
      }
    }

    /**
     * Open state for the dialog is driven by ModalModule.status (see
     * dialogVisibleForStatus). The matching computed exposes a setter that
     * ignores its argument and unconditionally calls setStatus(Hidden) when
     * the currently active modal matches, so any assignment closes the
     * dialog. We therefore react only to close events here; the open path
     * is handled by whoever sets ModalModule.status to PosConfig.
     */
    function onOpenChange(open: boolean) {
      if (open) {
        return;
      }
      handleBeforeClose(() => {
        modalVisibility.value = false;
      });
    }

    function resetProgress() {
      POSConfigModule.setPCsLoaded(0);
      POSConfigModule.setTimeslotsLoaded(0);
      POSConfigModule.setExtraProgress(0);
    }

    /**
     * Load pos configs based on the logged in user group
     * And set them to the state
     */
    async function loadConfigs() {
      const controller = new AbortController();
      abortController.value = controller;
      const signal = controller.signal;

      loading.value = true;
      userGroupId.value = UserModule.getUserGroupId;
      if (userGroupId.value) {
        try {
          await POSConfigModule.fetchUserConfigs(userGroupId.value);
          await POSConfigModule.fetchGroupInfo();
          // The group, and therefore the country, is only known now, so this is
          // the first point at which the country-specific checks can run.
          if (!checkUserSocialSecurityNumber()) {
            return;
          }
          // The Belgian compliance init used to run here, because it was the only
          // place guaranteed to be past fetchGroupInfo(). App.vue's sessionReady
          // watcher now waits on getGroupCountryCode as well, so it fires on the
          // same event this call was waiting for and initializes with the
          // "app-boot" trigger. A fifth call site would only have muddied Graylog.
        } catch (error: any) {
          if (
            error.message.includes(NetworkErrors.NoAccessToken) ||
            error.message.includes(NetworkErrors.NoSmtzAuthToken)
          ) {
            // already handled by logging out
            return;
          } else {
            // unexpected error, show a general response error
            NotificationService.showGeneralResponseError();
            return;
          }
        }

        if (signal.aborted) {
          loading.value = false;
          return;
        }

        // Pulling group pictures and storing them in Dexie
        await proceedFetchAndStoreGroupPicture(signal);
        ////

        if (signal.aborted) {
          loading.value = false;
          return;
        }

        // TODO HAMZA LOCATIONS WORK: the below is only for testing. We need to have a better design for locations logic
        LogService.info(`Fetching group cash locations`);
        const resp = await DeviceModule.fetchLocations();
        LogService.info(`Group cash locations pulled, attached.`, {
          params: resp,
        });
      } else if (UserModule.isAdmin) {
        await AuthModule.UserLogOut();
        // remove gId parameter if it is set
        if (AppModule.gid) {
          AppModule.removeGID();
        }
        return;
      } else {
        hideDialog();
        NotificationService.showCustomError(t("error.no-access-right"));
        await AuthModule.UserLogOut();
        // remove gId parameter if it is set
        if (AppModule.gid) {
          AppModule.removeGID();
        }
        await redirectToLoginPage();
        return;
      }

      // @TODO Riyadh clean next line
      configs.value = POSConfigModule.posConfigs;
      loading.value = false;
    }
    async function confirmSelectConfig(config: IPOSConfig) {
      if (
        POSConfigModule.selectedConfig &&
        config !== POSConfigModule.selectedConfig &&
        hasNonEmptyOrders.value
      ) {
        const confirmed = await showConfirm({
          title: t("common.warning"),
          message: t("posconfiguration.change-config"),
          variant: "warning",
          confirmText: t("common.yes"),
          cancelText: t("common.return"),
        });

        if (confirmed) {
          selectConfig(config);
        } else {
          ModalModule.setStatus(ModalStatus.Hidden);
        }
      } else {
        selectConfig(config);
      }
    }
    async function selectConfig(config: IPOSConfig) {
      const controller = new AbortController();
      abortController.value = controller;
      const signal = controller.signal;

      loading.value = false;
      configLoading.value = true;
      POSConfigModule.SET_SELECTED_CONFIG(config);
      // clear the selected grid
      console.log("*********** calling selectGrid 4");
      POSConfigModule.selectGrid(null);

      const success = await loadConfigData(config, signal);

      if (signal.aborted) {
        LogService.info("Config selection aborted by user");
        resetProgress();
        configLoading.value = false;
        if (hasSalesStarted.value) {
          await ensureSelectedOrder();
        }
        return;
      }

      if (success) {
        // Wait for a short delay before hiding the modal
        setTimeout(async () => {
          if (signal.aborted) return;
          // It is sure that every data from remote to start selling is now pulled and stored in local db
          // lets close empty orders and create a fresh one if sales has already started
          if (hasSalesStarted.value) {
            removeEmptyOrders();
            try {
              await createNewOrder();
            } catch {
              // journaled + surfaced by the order helper; the cashier can
              // open an order from the navbar
            }
          }
          hideDialog();
          // lets reset loading progress
          resetProgress();
          configLoading.value = true;
          await pushWithQuery(RouteNames.Booking);
        }, 500);
      } else {
        // user is not allowed to sell using selected config
        configLoading.value = false;
        loadConfigs();
      }
    }

    return {
      t,
      modalVisibility,
      loadConfigs,
      userGroupId,
      configs,
      selectConfig,
      loading,
      adminUserWithoutGroup,
      progressValue,
      configLoading,
      confirmSelectConfig,
      getLoadingText,
      onOpenChange,
    };
  },

  async mounted() {
    this.loadConfigs();
  },
});
</script>
